/** biome-ignore-all lint/suspicious/noExplicitAny: any follows the redaxios body type */
import queryString from 'query-string'
import redaxios, { type Options, type Response } from 'redaxios'

export type ErrorCodes =
  | 'unknown_error'
  | 'network_error'
  | 'server_error'
  | 'bad_request'
  | 'unauthenticated'
  | 'unauthorized'
  | 'not_exist'
  | 'pre_exist'
  | 'resource_expired'
  | 'parallel_requests'

export type ErrorObj<C = ErrorCodes | string, T = unknown> = {
  error: true
  /** if unspecified would take place of the Error.name */
  code: C
  message: string
  /** will be reflected in HTTP return code too */
  statusCode: number
  stack?: string[]
  /** additional detail, might depend on the error code */
  detail?: T
}

const redaxiosInstance = redaxios.create({
  paramsSerializer: (p) => {
    if (!p) return ''
    if (p instanceof URLSearchParams) return p.toString()
    return queryString.stringify(p, { arrayFormat: 'comma', skipEmptyString: true, skipNull: true })
  },
})
class HttpClient {
  private redaxiosInstance: ReturnType<(typeof redaxios)['create']>
  constructor() {
    this.redaxiosInstance = redaxiosInstance
  }
  processResponse<T>(r: Response<T>) {
    if (r.headers.get('Content-Type')?.startsWith('application/json')) {
      const data = r.data
      if ((data as ErrorObj).error) throw data
      return r
    }
    return r
  }
  processError(oe: unknown) {
    const e = oe as any
    if (
      typeof e === 'object' &&
      e !== null &&
      'error' in e &&
      e.error === true &&
      'statusCode' in e &&
      typeof e.statusCode === 'number'
    ) {
      const error = e as ErrorObj
      console.error(error)
      throw error
    }
    if (typeof e === 'object' && e !== null && typeof (e as any).status === 'number') {
      // axios non-200 error
      if (
        e.data &&
        typeof e.data === 'object' &&
        e.data.error &&
        typeof e.data.statusCode === 'number'
      ) {
        // e.data is ErrorObj
        const error = e.data as ErrorObj
        console.error(error)
        throw error
      }
    }
    if (
      (typeof e.status === 'number' && e.status === 502) ||
      (e instanceof Error && e.name === 'TypeError' && e.message.includes('Failed to fetch'))
    ) {
      const error: ErrorObj = {
        error: true,
        code: 'network_error',
        message: 'Failed to communicate with server, please check your internet connection',
        detail: e,
        statusCode: -1,
      }
      console.error(error)
      throw error
    }
    const error: ErrorObj = {
      error: true,
      code: 'unknown_error',
      message: 'An unknown error has occured',
      detail: e,
      statusCode: -1,
    }
    console.error(error)
    throw error
  }
  async get<T>(u: string, o?: Options) {
    try {
      const response = await this.redaxiosInstance.get<T>(u, o)
      return this.processResponse(response)
    } catch (e) {
      this.processError(e)
    }
  }
  async post<T>(url: string, body?: any, opt?: Options) {
    try {
      const response = await this.redaxiosInstance.post<T>(url, body, opt)
      return this.processResponse(response)
    } catch (e) {
      this.processError(e)
    }
  }
  async put<T>(url: string, body?: any, opt?: Options) {
    try {
      const response = await this.redaxiosInstance.put<T>(url, body, opt)
      return this.processResponse(response)
    } catch (e) {
      this.processError(e)
    }
  }
  async patch<T>(url: string, body?: any, opt?: Options) {
    try {
      const response = await this.redaxiosInstance.patch<T>(url, body, opt)
      return this.processResponse(response)
    } catch (e) {
      this.processError(e)
    }
  }
  async delete<T>(url: string, opt?: Options) {
    try {
      const response = await this.redaxiosInstance.delete<T>(url, opt)
      return this.processResponse(response)
    } catch (e) {
      this.processError(e)
    }
  }
}

const httpClient = new HttpClient()

export default httpClient
